// ============================================================
// MongoDBTransactions.jsx
//
// A branded, technical guide to ACID Compliance and
// Multi-Document Transactions in MongoDB.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #F9D71C  (Amber / Transactional Safety)
//   Accent      → #00ED64  (MongoDB Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Alert
} from "@mui/material";

/* MUI Icons */
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import HistoryIcon from "@mui/icons-material/History";
import SecurityIcon from "@mui/icons-material/Security";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(249, 215, 28, 0.2)",
    amber: "#F9D71C",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.amber }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FDE68A", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBTransactions() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.amber }} />}
                        label="Data Integrity · Module 13"
                        sx={{ bgcolor: "rgba(249,215,28,0.1)", color: BRAND.amber, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Multi-Doc <span style={{ color: BRAND.amber }}>Transactions</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        In MongoDB, single-document operations are atomic by default. However, when you need to update
                        multiple documents across different collections, <strong>Transactions</strong> ensure your data
                        remains consistent even if a system failure occurs mid-process.
                    </Typography>
                </Box>

                {/* 2. ACID PROPERTIES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <VerifiedUserIcon sx={{ color: BRAND.amber, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>ACID Compliance</Typography>
                    </Box>
                    <Grid container spacing={2}>
                        {[
                            { f: "Atomicity", d: "All operations in the transaction succeed or none do (All-or-Nothing)." },
                            { f: "Consistency", d: "The database moves from one valid state to another, following all rules." },
                            { f: "Isolation", d: "Concurrent transactions do not interfere with each other." },
                            { f: "Durability", d: "Once committed, the data is permanent, even in a power outage." }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                    <Typography variant="subtitle1" sx={{ color: BRAND.amber, fontWeight: 900 }}>{item.f}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.muted }}>{item.d}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. TRANSACTION WORKFLOW (The Mongoose Way) */}
                <Grid container spacing={6} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <AccountBalanceIcon sx={{ color: BRAND.amber, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>The Banking Example</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Imagine transferring money: You must <strong>subtract</strong> from Account A and <strong>add</strong> to Account B.
                            If the server crashes after step 1, the money vanishes. Transactions prevent this using a <strong>Session</strong>.
                        </Typography>
                        <Alert severity="info" sx={{ bgcolor: "rgba(249,215,28,0.05)", color: BRAND.white, border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="body2">
                                <strong>Constraint:</strong> Transactions require a <strong>Replica Set</strong> or Sharded Cluster.
                                They do not work on standalone local instances.
                            </Typography>
                        </Alert>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="transaction_logic.js"
                            code={`const session = await db.startSession();\nsession.startTransaction();\n\ntry {\n  // 1. Subtract from User A\n  await User.updateOne({ _id: A }, { $inc: { bal: -100 } }, { session });\n  \n  // 2. Add to User B\n  await User.updateOne({ _id: B }, { $inc: { bal: 100 } }, { session });\n\n  await session.commitTransaction();\n} catch (error) {\n  await session.abortTransaction(); // Undo everything!\n} finally {\n  session.endSession();\n}`}
                        />
                    </Grid>
                </Grid>



                {/* 4. PERFORMANCE CONSIDERATIONS */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(249, 215, 28, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <SyncAltIcon sx={{ color: BRAND.amber }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Best Practices</Typography>
                    </Box>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    • <strong>Keep it short:</strong> Transactions lock documents. Long-running transactions slow down the whole cluster.
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    • <strong>Prefer Embedding:</strong> If you find yourself using transactions constantly, your schema might be too normalized. Try nesting data instead.
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    • <strong>60-Second Limit:</strong> By default, MongoDB aborts any transaction that lasts longer than 60 seconds.
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    • <strong>Write Conflict:</strong> If two transactions try to modify the same document, one will fail and must be retried.
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #6B5900)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <SecurityIcon style={{ fontSize: 40, color: BRAND.amber, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Integrity Guaranteed?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Now that you can handle complex data flows safely, let's explore how to keep the cluster alive with Replication.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.amber, color: BRAND.bg, fontWeight: 900 }}>Next: Replication</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Transaction Docs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
